import { IonButton, IonSpinner } from '@ionic/react'
import {
	maxAcceptedHvacTemperature,
	minAcceptedHvacTemperature,
	normalHvacTemperature,
} from '@vae/server/src/constants'
import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import useUser from '../../../../contexts/hooks/useUser'
import { trpc } from '../../../../modules/trpc'
import { HvacContext } from '../HvacContext'

const TemperatureControls = () => {
	const { t } = useTranslation('pages_hvac')

	const { hvac, setHvacValue, mutationLoading, actionStatus } = useContext(HvacContext)

	const { user } = useUser()

	const { data: userSettings, refetch } = trpc.user.userSettings.get.useQuery(undefined, {
		enabled: !!user?.id,
	})

	const { mutate: editUserSettings } = trpc.user.userSettings.edit.useMutation({
		onSuccess: () => {
			refetch()
		},
	})

	const setHvacTemperature = (adjustment: number) => {
		if (!hvac) return

		const currentActualHvacTemp = hvac.thermostatState.heatSetpoint ?? normalHvacTemperature

		const newTemp = Math.max(
			minAcceptedHvacTemperature,
			Math.min(maxAcceptedHvacTemperature, currentActualHvacTemp + adjustment)
		)

		if (!userSettings?.smartHeatingActive) {
			setHvacValue(hvac.id, {
				...hvac.thermostatState,
				mode: hvac.thermostatState.mode,
				heatSetpoint: (hvac?.thermostatState.heatSetpoint ?? normalHvacTemperature) + adjustment,
				coolSetpoint: (hvac?.thermostatState.coolSetpoint ?? normalHvacTemperature) + adjustment,
			})
		}

		editUserSettings({
			userId: user?.id ?? 0,
			data: { smartHeatingNormalTemperature: newTemp },
		})
	}

	return (
		<div className='flex flex-col gap-4 items-center py-6 border-y border-gray-100 dark:border-gray-600 relative'>
			<div className='absolute top-4 right-4'>
				{actionStatus?.state === 'PENDING' && (
					<div className='animate-pulse'>
						<IonSpinner color='primary' name='circles' />
					</div>
				)}
				{actionStatus?.state === 'CONFIRMED' && (
					<div className='w-6 h-6 bg-green-500 rounded-full flex items-center justify-center animate-bounce shadow-lg transform transition-all duration-300 ease-out scale-100 hover:scale-110'>
						<span className='text-white text-sm animate-pulse'>✓</span>
					</div>
				)}
				{actionStatus?.state === 'FAILED' && (
					<div className='w-6 h-6 bg-red-500 rounded-full flex items-center justify-center animate-pulse shadow-lg transform transition-all duration-300 ease-out scale-100 hover:scale-110'>
						<span className='text-white text-sm'>!</span>
					</div>
				)}
			</div>

			<div className='text-sm font-medium text-gray-700 dark:text-gray-200 uppercase tracking-wide'>
				{t('generalTemperature')}
			</div>
			{userSettings?.smartHeatingActive ? (
				<div className='text-sm text-gray-700 dark:text-white/80 text-center px-4'>
					{t('smartHvacDisclaimer')}
				</div>
			) : null}
			{hvac?.thermostatState.mode !== 'OFF' && (
				<div
					className={`ellipsis-buttons flex gap-8 ${
						mutationLoading ||
						(actionStatus?.target && actionStatus.target.mode !== hvac?.thermostatState.mode)
							? 'cursor-wait'
							: ''
					}`}
				>
					<IonButton
						fill='solid'
						className='w-12 h-12 --border-radius:8px m-0'
						style={{
							'--background': '#4a7c7a',
							'--background-hover': '#3a6b69',
							'--color': 'white',
							'--border-radius': '8px',
							width: '48px',
							height: '48px',
							margin: '0',
						}}
						onClick={() => setHvacTemperature(-1)}
						disabled={
							(userSettings?.smartHeatingNormalTemperature ?? normalHvacTemperature) <=
							minAcceptedHvacTemperature
						}
					>
						-
					</IonButton>
					<div className='flex items-center justify-center min-w-[100px] bg-white/20 dark:bg-black/20 rounded-lg px-4 py-2'>
						<span className='text-4xl font-bold text-gray-900 dark:text-white drop-shadow-lg'>
							{userSettings?.smartHeatingActive
								? userSettings?.smartHeatingNormalTemperature
								: hvac?.thermostatState.heatSetpoint}
						</span>

						<span className='text-xl text-gray-900/90 dark:text-white/90 ml-1 drop-shadow-md'>
							°C
						</span>
					</div>

					<IonButton
						fill='solid'
						className='w-12 h-12 --border-radius:8px m-0'
						style={{
							'--background': '#4a7c7a',
							'--background-hover': '#3a6b69',
							'--color': 'white',
							'--border-radius': '8px',
							width: '48px',
							height: '48px',
							margin: '0',
						}}
						onClick={() => setHvacTemperature(1)}
						disabled={
							(userSettings?.smartHeatingSavingTemperature ?? minAcceptedHvacTemperature) >=
							maxAcceptedHvacTemperature
						}
					>
						+
					</IonButton>
				</div>
			)}
		</div>
	)
}
export default TemperatureControls
